import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { useEffect, useState } from 'react';
import * as API from '../../API';

import { CircularProgress, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import type TextBlock from '../../Models/TextBlock';

function ReportTextBlock({ procedureIDs }: Readonly<{ procedureIDs: number[] }>): React.ReactElement {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [allTextBlocks, setAllTextBlocks] = useState<TextBlock[]>([]);
    const [filteredTextBlocks, setFilteredTextBlocks] = useState<TextBlock[]>([]);

    useEffect(() => {
        API.textblocks.getReportingTextBlocksForRadiologist(procedureIDs)
            .then((blocks) => {
                setAllTextBlocks(blocks.TextBlocks);
                setFilteredTextBlocks(blocks.TextBlocks);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error getting textblocks for radiologist', err);
            });
    }, [procedureIDs]);

    const filter = (searchTerm: string): void => {
        if (searchTerm === '') {
            setFilteredTextBlocks(allTextBlocks);
            return;
        }
        const search = (x: TextBlock): boolean => {
            const term = searchTerm.toLowerCase();
            return x.Name.toLowerCase().includes(term)
                || x.SearchTerm.toLowerCase().includes(term)
                || x.Pronunciation.toLowerCase().includes(term);
        };
        const blocks = allTextBlocks.filter(search);
        setFilteredTextBlocks(blocks);
    };

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <TextField label={t('Search')} variant="outlined" onChange={(e) => filter(e.target.value)} size="small" />
            </Grid>
            <Grid size={12}>
                <TableContainer component={Paper} sx={{ maxHeight: '600px' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {/* TextAlign left is necessary here to overwrite datatables.css overzealous styling... */}
                                <TableCell />
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('Name')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('SearchTerm')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('Pronunciation')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            { loading
                                ? (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center' }}>
                                                <CircularProgress />
                                            </TableCell>
                                        </TableRow>
                                    )
                                : filteredTextBlocks.map((x) => <TextBlockRow key={x.ID} textBlock={x} />) }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    );
}

function TextBlockRow({ textBlock }: Readonly<{ textBlock: TextBlock }>): React.ReactElement {
    const [showMore, setShowMore] = useState(false);

    const showContent = (e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();
        setShowMore(!showMore);
    };

    const insertTextBlock = (): void => {
        // @ts-ignore This is handled by ReportShared.js, and will also handle closing this dialog
        ReportSharedFunctions.setContentAtCursor(textBlock.Content);
    };

    return (
        <>
            <TableRow onClick={insertTextBlock} hover sx={{ cursor: 'pointer' }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={showContent}>
                        {showMore ? <span className="material-symbols-outlined">keyboard_arrow_up</span> : <span className="material-symbols-outlined">keyboard_arrow_down</span>}
                    </IconButton>
                </TableCell>
                <TableCell>{textBlock.Name}</TableCell>
                <TableCell>{textBlock.SearchTerm}</TableCell>
                <TableCell>{textBlock.Pronunciation}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={showMore} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <div dangerouslySetInnerHTML={{ __html: textBlock.Content }} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default ReportTextBlock;
